import type { PpeIssuanceRecord, PpeNorm, PpeNormDetail, PpeProcurementRow } from '../model/ppeSchema';

/** GET /api/ppe/norms — справочник норм СИЗ. Бэкенд: effective_from ISO; quantity/period — строки. */
export const ppeNorms: PpeNorm[] = [
  {
    id: 1,
    position: 'Электросварщик',
    etksRank: '5',
    conditionClass: '3.2',
    ppeName: 'Щиток сварщика НН',
    quantity: '1 шт.',
    period: '12 мес.',
    effectiveFrom: '01.01.2025',
  },
  {
    id: 2,
    position: 'Электросварщик',
    etksRank: '5',
    conditionClass: '3.2',
    ppeName: 'Перчатки сварщика',
    quantity: '12 пар',
    period: '1 мес.',
    effectiveFrom: '01.01.2025',
  },
  {
    id: 3,
    position: 'Электросварщик',
    ppeName: 'Костюм для сварщика',
    quantity: '1 шт.',
    period: '12 мес.',
    effectiveFrom: '01.01.2025',
  },
  {
    id: 4,
    position: 'Электросварщик',
    etksRank: '6',
    conditionClass: '3.3',
    ppeName: 'Респиратор РПГ-67',
    quantity: '24 шт.',
    period: '1 мес.',
    effectiveFrom: '01.01.2025',
  },
  {
    id: 5,
    position: 'Токарь',
    conditionClass: '2',
    ppeName: 'Очки защитные ЗП14',
    quantity: '1 шт.',
    period: '12 мес.',
    effectiveFrom: '01.03.2025',
  },
  {
    id: 6,
    position: 'Токарь',
    conditionClass: '2',
    ppeName: 'Перчатки х/б',
    quantity: '12 пар',
    period: '1 мес.',
    effectiveFrom: '01.03.2025',
  },
  {
    id: 7,
    position: 'Крановщик',
    etksRank: '4',
    conditionClass: '3.1',
    ppeName: 'Каска защитная',
    quantity: '1 шт.',
    period: '24 мес.',
    effectiveFrom: '01.01.2025',
  },
];

const ppeNormDetailExtras: Partial<
  Record<number, Pick<PpeNormDetail, 'gostTu' | 'careInstructions' | 'substitutionRule'>>
> = {
  1: {
    gostTu: 'ГОСТ 12.4.023-84 (тип С)',
    careInstructions: 'Хранить в сухом месте; протирать мягкой тканью без растворителей.',
    substitutionRule: 'Допускается аналог по согласованию с отделом ОТ.',
  },
  2: {
    gostTu: 'ГОСТ EN 12477-2011',
    careInstructions: 'Сушить при комнатной температуре; не допускать намокания утеплителя/подкладки.',
    substitutionRule: 'Замена при прожогах/разрывах; допускается аналог с тем же классом защиты.',
  },
  3: {
    gostTu: 'ГОСТ EN ISO 11611-2014',
    careInstructions: 'Стирка по инструкции изготовителя; не использовать хлорсодержащие отбеливатели.',
    substitutionRule: 'Замена при повреждении защитных свойств или по результатам осмотра.',
  },
  4: {
    gostTu: 'ГОСТ Р 12.4.191-2022',
    careInstructions: 'Смена фильтров по графику; учёт в журнале выдачи.',
    substitutionRule: 'Только сертифицированные фильтрующие элементы.',
  },
  5: {
    gostTu: 'ГОСТ EN 166-2014',
    careInstructions: 'Протирать салфеткой для оптики; хранить в футляре во избежание царапин.',
    substitutionRule: 'Замена при царапинах/помутнении линз; допускается аналог с тем же классом защиты.',
  },
  6: {
    gostTu: 'ГОСТ EN 420-2011',
    careInstructions: 'Сушить после контакта с влагой; хранить в чистом сухом месте.',
    substitutionRule: 'Замена при износе/разрывах; допускается аналог по согласованию с ОТ.',
  },
  7: {
    gostTu: 'ГОСТ EN 397-2012',
    careInstructions: 'Не подвергать нагреву и воздействию растворителей; периодически осматривать корпус и оголовье.',
    substitutionRule: 'Замена при ударе, трещинах или деформациях независимо от срока носки.',
  },
};

export function buildPpeNormDetail(norm: PpeNorm): PpeNormDetail {
  const extra = ppeNormDetailExtras[norm.id];
  return {
    ...norm,
    gostTu: extra?.gostTu ?? 'По паспорту изделия (ГОСТ/ТУ производителя)',
    careInstructions: extra?.careInstructions ?? 'Общие правила хранения и ухода по инструкции изготовителя.',
    substitutionRule: extra?.substitutionRule ?? 'Замена по акту несоответствия или согласованию с ОТ.',
  };
}

/** GET /api/ppe/issuance — записи выдачи СИЗ. Бэкенд: даты ISO; service_life — строка. */
export const ppeIssuanceRecords: PpeIssuanceRecord[] = [
  {
    id: 1,
    employeeName: 'Кузнецов Р.Н.',
    ppeName: 'Щиток сварщика НН',
    quantity: '1 шт.',
    issueDate: '15.01.2026',
    expiryDate: '15.01.2026',
    serviceLife: '12 мес.',
  },
  {
    id: 2,
    employeeName: 'Кузнецов Р.Н.',
    ppeName: 'Перчатки сварщика',
    quantity: '12 пар',
    issueDate: '01.04.2026',
    expiryDate: '01.05.2026',
    serviceLife: '1 мес.',
  },
  {
    id: 3,
    employeeName: 'Кузнецов Р.Н.',
    ppeName: 'Костюм для сварщика',
    quantity: '1 шт.',
    issueDate: '15.01.2026',
    expiryDate: '15.01.2026',
    serviceLife: '12 мес.',
  },
  {
    id: 4,
    employeeName: 'Петров А.В.',
    ppeName: 'Каска защитная',
    quantity: '1 шт.',
    issueDate: '10.03.2026',
    expiryDate: '10.03.2026',
    serviceLife: '24 мес.',
  },
  {
    id: 5,
    employeeName: 'Петров А.В.',
    ppeName: 'Перчатки х/б',
    quantity: '12 пар',
    issueDate: '01.04.2026',
    expiryDate: '01.05.2026',
    serviceLife: '1 мес.',
  },
];

/** GET /api/ppe/procurement — потребность в СИЗ. Бэкенд: deficit = total_need - issued. */
export const ppeProcurement: PpeProcurementRow[] = [
  {
    position: 'Электросварщик',
    ppeName: 'Щиток сварщика НН',
    normQty: '1 шт.',
    period: '12 мес.',
    employeeCount: 12,
    totalNeed: 12,
    issued: 10,
    deficit: 2,
  },
  {
    position: 'Электросварщик',
    ppeName: 'Перчатки сварщика',
    normQty: '12 пар',
    period: '1 мес.',
    employeeCount: 12,
    totalNeed: 144,
    issued: 120,
    deficit: 24,
  },
  {
    position: 'Электросварщик',
    ppeName: 'Костюм для сварщика',
    normQty: '1 шт.',
    period: '12 мес.',
    employeeCount: 12,
    totalNeed: 12,
    issued: 11,
    deficit: 1,
  },
  {
    position: 'Электросварщик',
    ppeName: 'Респиратор РПГ-67',
    normQty: '24 шт.',
    period: '1 мес.',
    employeeCount: 12,
    totalNeed: 288,
    issued: 240,
    deficit: 48,
  },
  {
    position: 'Токарь',
    ppeName: 'Очки защитные ЗП14',
    normQty: '1 шт.',
    period: '12 мес.',
    employeeCount: 18,
    totalNeed: 18,
    issued: 18,
    deficit: 0,
  },
  {
    position: 'Токарь',
    ppeName: 'Перчатки х/б',
    normQty: '12 пар',
    period: '1 мес.',
    employeeCount: 18,
    totalNeed: 216,
    issued: 200,
    deficit: 16,
  },
  {
    position: 'Крановщик',
    ppeName: 'Каска защитная',
    normQty: '1 шт.',
    period: '24 мес.',
    employeeCount: 6,
    totalNeed: 6,
    issued: 6,
    deficit: 0,
  },
];
